<div class="site-analytics-scaffold-layout-container tw-relative tw-overflow-y-auto tw-h-full">
    <div class="site-analytics-layout-content">
        <mon-page-layout
            [header]="config.pageHeaderKey | transloco"
            [subheader]="config.pageSubheaderKey | transloco"
        >
            <ng-content select="[siteAnalyticsScaffoldBeforeControls]"></ng-content>

            <div class="site-analytics-scaffold__controls">
                <div class="site-analytics-scaffold__control">
                    <label for="site-analytics-primary-date-range">
                        {{ config.dateRangeLabelKey | transloco }}
                    </label>
                    <mon-site-analytics-date-range-picker
                        inputId="site-analytics-primary-date-range"
                        [ngModel]="primaryDateRange"
                        (ngModelChange)="onPrimaryDateRangeChange($event)"
                        data-test="site-analytics-primary-date-range"
                    />
                </div>

                @if (isComparisonSupported) {
                    <div class="site-analytics-scaffold__toggle-control">
                        <p-toggleswitch
                            inputId="site-analytics-comparison-toggle"
                            [ngModel]="comparisonEnabled"
                            (onChange)="onComparisonToggleChange($event)"
                            data-test="site-analytics-comparison-toggle"
                        />
                        <label for="site-analytics-comparison-toggle" class="tw-mb-0">
                            {{ config.compareToggleLabelKey | transloco }}
                        </label>
                    </div>

                    @if (comparisonEnabled) {
                        <div class="site-analytics-scaffold__control">
                            <label for="site-analytics-comparison-date-range">
                                {{ config.compareDateRangeLabelKey | transloco }}
                            </label>
                            <mon-site-analytics-date-range-picker
                                inputId="site-analytics-comparison-date-range"
                                [ngModel]="comparisonDateRange"
                                (ngModelChange)="onComparisonDateRangeChange($event)"
                                data-test="site-analytics-comparison-date-range"
                            />
                        </div>
                    }
                }
            </div>

            @if (breadcrumbItems.length > 0) {
                <div subheader-extra>
                    <mon-breadcrumb [model]="breadcrumbItems" />
                </div>
            }

            @if (shouldShowChart) {
                <p-card styleClass="site-analytics-scaffold__chart-card tw-mb-8" data-test="site-analytics-chart-card">
                    <div class="site-analytics-scaffold__chart-container">
                        <p-chart
                            type="bar"
                            [data]="chartData"
                            [options]="chartOptions"
                            height="100%"
                            data-test="site-analytics-chart"
                        />
                        @if (!loading && !hasChartData) {
                            <div class="site-analytics-scaffold__chart-empty" data-test="site-analytics-chart-empty">
                                <span class="tw-text-gray-500 tw-font-[300] tw-text-xl">
                                    {{ chartEmptyMessageKey | transloco }}
                                </span>
                            </div>
                        }
                    </div>
                </p-card>
            }

            <mon-table-wrapper
                [value]="tableRows"
                [headers]="tableHeaders"
                [total]="total >= perPage ? total + 1 : total"
                [currentPage]="page"
                [perPage]="perPage"
                [loading]="loading"
                [fixedTableLayout]="true"
                [sortBy]="sortBy"
                [sortDir]="sortDir"
                [searchText]="isSearchSupported ? searchTerm : undefined"
                [searchAriaDescribedBy]="'site-analytics-search-help'"
                [searchPlaceholder]="config.searchPlaceholderKey | transloco"
                [searchResultsAnnouncement]="searchResultsAnnouncement"
                [emptyMessage]="'siteAnalytics.summary.shared.emptyState' | transloco"
                [emptySearchMessage]="'siteAnalytics.summary.shared.emptySearchState' | transloco"
                [exportConfig]="exportConfig"
                [exportButtonLabel]="config.exportButtonLabelKey | transloco"
                [tableKey]="config.tableKey"
                data-test="site-analytics-table"
                (searchInput)="onSearchInput($event)"
                (page)="onPageChange($event)"
                (sort)="onSort($event)"
                [showPageLinks]="false"
                [showFirstLastIcon]="false"
            >
                <span monTableCaption>{{ config.tableCaptionKey | transloco }}</span>

                @if (showExportVariantSelector) {
                    <div monTableRight class="site-analytics-scaffold__export-variant-select">
                        <label class="tw-font-medium" for="site-analytics-export-variant">
                            {{ 'siteAnalytics.scaffold.export.variantLabel' | transloco }}
                        </label>
                        <p-select
                            inputId="site-analytics-export-variant"
                            [options]="exportVariantOptions"
                            [ngModel]="selectedExportVariantId"
                            (ngModelChange)="onExportVariantChange($event)"
                            optionLabel="label"
                            optionValue="id"
                            appendTo="body"
                            styleClass="tw-min-w-56"
                            data-test="site-analytics-export-variant"
                        />
                    </div>
                }

                <ng-template #cells let-row let-field="field">
                    @if (isHierarchyToggleColumn(field)) {
                        @if (isHierarchyParentRow(row)) {
                            <p-button
                                type="button"
                                variant="text"
                                [icon]="isHierarchyExpanded(row) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                (click)="toggleHierarchyRow(row)"
                                (onKeyDown)="onHierarchyToggleKeyDown($event, row)"
                                [attr.aria-label]="getHierarchyToggleAriaLabel(row)"
                                data-test="site-analytics-hierarchy-toggle"
                            />
                        }
                    } @else {
                        @if (getLinkCell(row, field); as linkCell) {
                            @if (isStateLinkCell(linkCell)) {
                                <a
                                    class="p-link-with-icon tw-no-underline tw-flex tw-items-center tw-gap-2 tw-min-w-0"
                                    [uiSref]="linkCell.state"
                                    [uiParams]="linkCell.params || {}"
                                    [attr.aria-label]="getLinkAriaLabel(field, linkCell)"
                                    [attr.title]="linkCell.label"
                                    data-test="site-analytics-state-link"
                                >
                                    <span class="tw-underline tw-truncate tw-min-w-0">{{ linkCell.label }}</span>
                                </a>
                            } @else {
                                <span class="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
                                    @if (linkCell.tooltip) {
                                        <span class="tw-truncate" [attr.title]="linkCell.href">{{ linkCell.tooltip }}</span>
                                    }
                                    <a
                                        class="p-link-with-icon tw-no-underline tw-flex tw-items-center tw-gap-2 tw-min-w-0"
                                        [href]="linkCell.href"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        [attr.aria-label]="getLinkAriaLabel(field, linkCell)"
                                        [attr.title]="linkCell.href"
                                        data-test="site-analytics-url-link"
                                    >
                                        <mon-svg-icon name="openInNew" />
                                        <span class="tw-underline tw-truncate">{{ linkCell.label }}</span>
                                    </a>
                                </span>
                            }
                        } @else if (isDurationColumn(field)) {
                            {{ formatDuration(getNumericCellValue(row, field)) }}
                        } @else if (isNumberColumn(field)) {
                            {{ formatNumber(getNumericCellValue(row, field)) }}
                        } @else {
                            <span class="tw-truncate tw-block" [attr.title]="getTextCellValue(row, field)">{{ getTextCellValue(row, field) }}</span>
                        }
                    }
                </ng-template>
            </mon-table-wrapper>

            <span id="site-analytics-search-help" class="tw-sr-only">
                {{ 'siteAnalytics.scaffold.search.a11yDescription' | transloco }}
            </span>
        </mon-page-layout>
    </div>
</div>
